import { Button, cn, Stack, Tooltip } from '@prenuvo/halo-foundation';

import { FCT } from '@/i18n';
import { formatTimestampToDate } from '@/shared/util';
import { StudyResult } from '@/store';

import { getSkuPackage } from '../../../../util';
import { PatientDoc } from './patient-docs.model';
import { BUTTON_CLASS_OVERRIDES } from './patient-docs-panel.constant';
import { generatePreviousStudyReportPdfLink } from './pdf-docs.util';

interface PatientDocsProps {
  mifLink: string;
  onPatientLinkChange: (value: PatientDoc) => void;
  pdfLink: PatientDoc;
  previousStudyIds: StudyResult[];
}

export function PatientDocs({
  mifLink,
  onPatientLinkChange,
  pdfLink,
  previousStudyIds,
}: PatientDocsProps) {
  const displayFormattedSKU = (skuIds: string[]) =>
    skuIds.map((id) => getSkuPackage(Number(id))).join(' ');

  return (
    <Stack>
      <Tooltip
        className="bg-stone-700 text-xs text-neutral-50 dark:bg-stone-700 dark:text-neutral-50"
        content={FCT.REPORTING.HEADER.ACTIONS.MEDICAL_HISTORY}
      >
        <div>
          <Button
            aria-label={FCT.REPORTING.HEADER.ACTIONS.MEDICAL_HISTORY}
            className={{
              root: cn({
                [BUTTON_CLASS_OVERRIDES]: pdfLink.url.includes('medical-history'),
              }),
            }}
            data-testid="medical-history-button"
            onClick={() => onPatientLinkChange({ type: 'html', url: mifLink })}
            size="sm"
            variant="text"
          >
            {FCT.REPORTING.HEADER.ACTIONS.PT_HX}
          </Button>
        </div>
      </Tooltip>

      {previousStudyIds.map(({ id, date, skuIDs }, index: number) => {
        const link = generatePreviousStudyReportPdfLink(id);

        return (
          <Tooltip
            key={`previous-report-${id}`}
            className="bg-stone-700 text-xs text-neutral-50 dark:bg-stone-700 dark:text-neutral-50"
            content={`${displayFormattedSKU(skuIDs)}, ${formatTimestampToDate(date)}`}
          >
            <div>
              <Button
                aria-label="PreviousReport"
                className={{
                  root: cn('px-3', {
                    [BUTTON_CLASS_OVERRIDES]: pdfLink.url === link,
                  }),
                }}
                data-testid={`previous-report-btn-${index}`}
                onClick={() => onPatientLinkChange({ type: 'pdf', url: link })}
                size="sm"
                variant="text"
              >
                P{index + 1}
              </Button>
            </div>
          </Tooltip>
        );
      })}
    </Stack>
  );
}
